import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import './Portfolio.css';
import './Modal.css';

import imageFileNames from '../assets/image-list.json';

const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

const Portfolio = () => {
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showAll, setShowAll] = useState(false);
  const modalRef = useRef(null);

  const openModal = useCallback((index) => {
    setSelectedImageIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImageIndex(null);
    document.body.style.overflow = 'auto';
  }, []);

  const showNextImage = useCallback(() => {
    if (selectedImageIndex === null) return;
    const nextIndex = (selectedImageIndex + 1) % images.length;
    setSelectedImageIndex(nextIndex);
  }, [selectedImageIndex, images.length]);

  const showPrevImage = useCallback(() => {
    if (selectedImageIndex === null) return;
    const prevIndex = (selectedImageIndex - 1 + images.length) % images.length;
    setSelectedImageIndex(prevIndex);
  }, [selectedImageIndex, images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') showNextImage();
      if (e.key === 'ArrowLeft') showPrevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    if (selectedImageIndex !== null && modalRef.current) {
      modalRef.current.focus();
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, closeModal, showNextImage, showPrevImage]);

  const toggleShowAll = useCallback(() => {
    setShowAll(!showAll);
  }, [showAll]);

  const importImages = async () => {
    try {
      const imported = await Promise.all(
        imageFileNames.map(async (fileName) => {
          const mod = await import(`../assets/images/${fileName}`);
          return mod.default;
        })
      );
      setImages(shuffleArray(imported));
    } catch (error) {
      console.error("Error importing images:", error);
    }
  };

  useEffect(() => {
    importImages();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    let timeoutId = null;
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 250);
    };
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, []);

  const imagesToShow = showAll ? images : images.slice(0, 6);

  return (
    <section className="portfolio-container" aria-labelledby="portfolio-title">
      <h2 className="portfolio-title">Our Recent Work in Northridge</h2>

      {isMobile ? (
        <Swiper
          spaceBetween={15}
          slidesPerView={'auto'}
          loop={images.length > 3}
          centeredSlides={true}
          className="portfolio-swiper"
        >
          {images.map((image, index) => (
            <SwiperSlide key={index} onClick={() => openModal(index)} className="portfolio-slide">
              <img
                src={image}
                alt={`Professional Handyman Work Northridge ${index + 1}`}
                className="portfolio-image"
                loading="lazy"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <>
          <div className="portfolio-grid">
            {imagesToShow.map((image, index) => (
              <div className="portfolio-item" key={index} onClick={() => openModal(index)}>
                <img
                  src={image}
                  alt={`Professional Handyman Work Northridge ${index + 1}`}
                  className="portfolio-image"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          {images.length > 6 && (
            <button onClick={toggleShowAll} className="show-more-button">
              {showAll ? 'Show Less' : 'Show More'}
            </button>
          )}
        </>
      )}

      {selectedImageIndex !== null && (
        <div
          className="modal-backdrop"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery view"
          ref={modalRef}
          tabIndex={-1}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} role="document">
            <button onClick={showPrevImage} className="modal-nav-button prev" aria-label="Previous image">&lt;</button>
            <img src={images[selectedImageIndex]} alt={`Enlarged view of handyman work ${selectedImageIndex + 1}`} className="modal-image" />
            <button onClick={showNextImage} className="modal-nav-button next" aria-label="Next image">&gt;</button>
            <button onClick={closeModal} className="modal-close-button" aria-label="Close">&times;</button>
            <div className="modal-counter">
              {selectedImageIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
